/* What Zana has been worth (2026-10-29, Gabriel: "put the savings card on the
   full Zana page … I want to see what it is"): the classic Zana Mode savings
   ledger, in the console's design. Each line is a priced problem Zana raised —
   a bus burning more than its own normal, an undeclared refill — that has since
   stopped: litres × the pump price on file. Money that stopped being at risk,
   not money banked, so the words are "worth", never "saved".

   Classic hid the card until there was a figure. Here an empty ledger says
   what will be counted, with one example line marked as an example, so the
   card can be judged before a school has earned a number. Zana tier only.

   Titled just "Savings" (Gabriel, 2026-10-29): he first read the card as what
   the school spends on Zana, and a longer title with a line under it was too
   much. */
import React, { useEffect, useState } from 'react';
import { fetchZanaSavings, ZanaSavings } from '../services/briefingsApi';
import { n0, plural } from './kit';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/** "2026-03" → "March 2026". */
const monthWords = (key: string | null) => {
  const [y, m] = String(key || '').split('-');
  return MONTHS[Number(m) - 1] ? `${MONTHS[Number(m) - 1]} ${y}` : null;
};

export const ZanaSavingsCard: React.FC<{ orgCode: string }> = ({ orgCode }) => {
  const [savings, setSavings] = useState<ZanaSavings | null | 'loading'>('loading');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!orgCode) return undefined;
    let live = true;
    fetchZanaSavings(orgCode).then((s) => { if (live) setSavings(s); });
    return () => { live = false; };
  }, [orgCode]);

  if (savings === 'loading') return null;
  const has = !!savings && savings.lifetimeKES > 0;
  const since = has ? monthWords(savings.firstMonth) : null;

  return (
    <div className="panel" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }} aria-label="Savings">
      <b style={{ fontSize: '15px' }}>Savings</b>
      {has ? (
        <>
          <b className="num" style={{ fontSize: '26px', lineHeight: '30px', fontFamily: "'Barlow Semi Condensed', Arial, sans-serif" }}>Ksh {n0(savings.lifetimeKES)}</b>
          <span className="sm" style={{ color: 'var(--muted)' }}>
            {since ? `Since ${since}` : 'So far'} · {savings.thisMonthKES > 0
              ? `Ksh ${n0(savings.thisMonthKES)} this month`
              : savings.lastMonthKES > 0 ? `Ksh ${n0(savings.lastMonthKES)} last month` : plural(savings.entryCount, 'fix', 'fixes')}
          </span>
          <button type="button" className="btn s b-line" style={{ alignSelf: 'flex-start' }} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? 'Hide what it is made of' : 'What is it made of?'}
          </button>
          {open && (
            <>
              {savings.basis.map((b, i) => (
                <div key={`${b.title}-${b.fixedAt}-${i}`} className="row" style={{ gap: '10px', justifyContent: 'space-between', borderTop: '1px solid var(--line)', paddingTop: '8px' }}>
                  <span className="sm" style={{ minWidth: 0 }}>{b.title}</span>
                  <b className="sm num" style={{ whiteSpace: 'nowrap' }}>Ksh {n0(b.savingsKES)}</b>
                </div>
              ))}
              <span className="sm" style={{ color: 'var(--muted)', fontSize: '12.5px' }}>
                Each line is a problem Zana raised that has stopped: litres × the pump price on file. Money no longer at risk, not money banked.
              </span>
            </>
          )}
        </>
      ) : (
        <>
          <span className="sm" style={{ color: 'var(--muted)' }}>
            Nothing yet. When a problem Zana spotted is fixed, what it was costing is counted here.
          </span>
          <div style={{ border: '1px dashed var(--line2)', borderRadius: '10px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span className="chip c-mute" style={{ alignSelf: 'flex-start', height: '22px' }}>Example</span>
            <span className="sm" style={{ color: 'var(--muted)' }}><b className="num" style={{ color: 'var(--ink2)' }}>Ksh 21,400</b> · a bus back to its normal fuel use after a service</span>
          </div>
        </>
      )}
    </div>
  );
};
